import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

const FirstSellerSignInPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Prevent body scroll when modal is shown
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleAddDetails = () => {
    navigate("/maltora-seller/profile-management");
  };

  const handleCancel = () => {
    navigate("/maltora-seller/dashboard/sales");
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={handleOverlayClick}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid #e2e0d8",
          borderRadius: "14px",
          width: "100%",
          maxWidth: "420px",
          overflow: "hidden",
          animation: "pop-in 0.2s ease",
          "@keyframes pop-in": {
            from: {
              opacity: 0,
              transform: "scale(0.96) translateY(8px)",
            },
            to: {
              opacity: 1,
              transform: "scale(1) translateY(0)",
            },
          },
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Modal Body */}
        <Box sx={{ padding: "28px 28px 0" }}>
          {/* Icon Circle */}
          <Box
            sx={{
              width: "46px",
              height: "46px",
              borderRadius: "50%",
              backgroundColor: "#e6f1fb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "18px",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#185fa5"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
              <polyline points="16 11 18 13 22 9" />
            </svg>
          </Box>

          {/* Title */}
          <Typography
            id="modal-title"
            sx={{
              fontSize: "17px",
              fontWeight: 600,
              color: "#1a1a18",
              marginBottom: "10px",
              lineHeight: 1.4,
            }}
          >
            Welcome! One quick step before you begin
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: "13px",
              color: "#5f5e5a",
              lineHeight: 1.6,
            }}
          >
            Since this is your first sign-in, we recommend completing your
            seller profile.
            <br />
            Please add your company and banking details.
          </Typography>
        </Box>

        {/* Info Box */}
        <Box
          sx={{
            margin: "20px 28px 0",
            padding: "12px 14px",
            backgroundColor: "#faeeda",
            border: "1px solid #FAC775",
            borderRadius: "8px",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#854F0B"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: "1px" }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#633806",
              lineHeight: 1.5,
            }}
          >
            You can skip this now and fill later under the{" "}
            <strong style={{ fontSize: "12px" }}>Profile Management</strong>{" "}
            section.
          </Typography>
        </Box>

        {/* Modal Footer */}
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            padding: "20px 28px 26px",
            justifyContent: "flex-end",
          }}
        >
          {/* Cancel Button */}
          <Box
            component="button"
            onClick={handleCancel}
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "9px 20px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "7px",
              transition: "background 0.15s",
              outline: "none",
              backgroundColor: "transparent",
              color: "#5f5e5a",
              border: "1px solid #d0cec6",
              "&:hover": {
                backgroundColor: "#f5f5f3",
              },
            }}
          >
            Cancel
          </Box>

          {/* Add Details Button */}
          <Box
            component="button"
            onClick={handleAddDetails}
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "9px 20px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "7px",
              transition: "background 0.15s",
              outline: "none",
              backgroundColor: "#BA7517",
              color: "#ffffff",
              border: "none",
              "&:hover": {
                backgroundColor: "#854F0B",
              },
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Add details
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FirstSellerSignInPage;
